import { createContext, FC, ReactNode, useCallback, useContext, useMemo } from 'react'
import { AppTester } from '../../../supporting'
import type { SortingTypeIndex } from '../../constants/sortingType'
import { DisplayType } from '../../constants/displayType'
import useQueryState from "../../hooks/useQueryState";

const DATE_SORT = 1;
const ORG_SORT = 3;
const POP_SORT = 2;
const ACT_SORT = 20;
const MEDIA_ALL = 50;
//const NAME_SORT = 3;
//const NULL_SORT = 0;

type EncodedStateFilterContextT = {
  sorting: SortingTypeIndex;
  tagIds: string[];
  categoryIds: string[];
  display: DisplayType;
  coords: string;
  zoom: number;
  setSorting: (sorting: SortingTypeIndex) => void;
  setZoom: (zoom: number) => void;
  setDisplay: (display: DisplayType) => void;
  setCoords: (coords: string) => void;
  setTagIds: (tags: string[]) => void;
  setCategoryIds: (categoryIds: string[]) => void;
};

const getInitialSortingBase = (): SortingTypeIndex | null => {
  if (typeof window !== 'undefined') {
    if (AppTester.isDeziiignContext()) {
      return DATE_SORT;
    }
    if (window.location.pathname === '/feed') {
      return DATE_SORT;
    }
    // if (window.location.search?.includes("tab=edu-channels")) {
    //   return MEDIA_ALL;
    // }

    if (AppTester.isExamHSE()) {
      return POP_SORT;
    }

    if (AppTester.isLectureMediiia() || AppTester.isStartHSE()) {
      return DATE_SORT;
    }

    if (window.location.pathname.startsWith('/teams/') || window.location.pathname.startsWith('/special')) {
      return null;
    }

    const storedSorting: unknown = localStorage.getItem('brg_sorting');
    if (storedSorting) {
      return storedSorting as SortingTypeIndex;
    }

    return AppTester.isSuperPuper() || AppTester.isLMSHSE() ? DATE_SORT : ACT_SORT;
  }
  return ACT_SORT;
}

const initialCoordsBase = '53.7091468307233,37.04320818185807';

const EncodedStateFilterContext = createContext<EncodedStateFilterContextT>({
  sorting: ACT_SORT,
  tagIds: [],
  categoryIds: [],
  display: DisplayType.grid,
  coords: initialCoordsBase,
  zoom: 0,
  setSorting: () => undefined,
  setZoom: () => undefined,
  setDisplay: () => undefined,
  setCoords: () => undefined,
  setTagIds: () => undefined,
  setCategoryIds: () => undefined,
});

type PropsT = {
  encodedState: string | null;
  initialSorting?: SortingTypeIndex;
  initialZoom?: number;
  initialDisplay?: DisplayType;
  initialCoords?: number[];
  initialTagIds?: string[],
  initialCategoryIds?: string[],
  children?: ReactNode
};

export const EncodedStateFilterProvider: FC<PropsT> = ({
  children,
  encodedState,
  initialSorting = getInitialSortingBase(),
  initialZoom = 5,
  initialDisplay = DisplayType.grid,
  initialCoords = initialCoordsBase,
  initialTagIds = [],
  initialCategoryIds = [],
}) => {

  const [state, setQueryParams] = useQueryState();

  const getInitialSorting = useMemo(() => {
    if (initialSorting) {
      return initialSorting;
    }
    const baseSorting = getInitialSortingBase();
    return baseSorting || ACT_SORT;
  }, [initialSorting]);

  const defaultEncodedState = useMemo(() => ({
    sorting: getInitialSorting,
    display: initialDisplay,
    coords: initialCoords,
  }), [getInitialSorting, initialZoom, initialDisplay, initialCoords, initialTagIds, initialCategoryIds]);

  const decodedState = useMemo(
    () => state,
    [encodedState, defaultEncodedState]
  );

  const sorting = useMemo(() => decodedState.sorting || getInitialSorting, [decodedState, getInitialSorting]);
  const zoom = useMemo(() => decodedState.zoom || initialZoom, [decodedState, initialZoom]);
  const display = useMemo(() => decodedState.display || initialDisplay, [decodedState, initialDisplay]);
  const coords = useMemo(() => decodedState.coords || initialCoords, [decodedState, initialCoords]);
  const tagIds = useMemo(() => decodedState.tagIds || initialTagIds, [decodedState, initialTagIds]);
  const categoryIds = useMemo(() => decodedState.categoryIds || initialCategoryIds, [decodedState, initialCategoryIds]);

  const handleChange = useCallback((payload: Partial<EncodedStateFilterContextT>) => {
    setQueryParams({
      sorting,
      ...(display === 'map' && { display }),
      ...payload,
    }, { replace: true });
  }, [
    sorting,
    display,
    defaultEncodedState,
  ]);

  const setSorting = useCallback((sorting: SortingTypeIndex) => {
    if ([1, 2, 20, 30].includes(sorting)) {
      localStorage.setItem('brg_sorting', String(sorting));
    }
    handleChange({ sorting })
  }, [handleChange]);
  const setZoom = useCallback((zoom: number) => handleChange({ zoom }), [handleChange]);
  const setDisplay = useCallback((display: DisplayType) => handleChange({ display }), [handleChange]);
  const setCoords = useCallback((coords: string) => handleChange({ coords }), [handleChange]);
  const setTagIds = useCallback((tagIds: string[]) => handleChange({ tagIds }), [handleChange]);
  const setCategoryIds = useCallback((categoryIds?: string[]) => handleChange({ categoryIds }), [handleChange]);

  const value = useMemo(() => ({
    sorting,
    zoom,
    display,
    coords: coords.split(','),
    tagIds,
    categoryIds,
    setSorting,
    setZoom,
    setDisplay,
    setCoords,
    setTagIds,
    setCategoryIds,
  }), [
    sorting,
    zoom,
    display,
    coords,
    tagIds,
    categoryIds,
    setSorting,
    setZoom,
    setDisplay,
    setCoords,
    setTagIds,
    setCategoryIds,
  ]);

  return (
    <EncodedStateFilterContext.Provider value={value}>
      {children}
    </EncodedStateFilterContext.Provider>
  );
};

export const useEncodedStateFilter = (): EncodedStateFilterContextT => {
  const context = useContext(EncodedStateFilterContext);

  if (context == null) {
    throw new Error('useEncodedStateFilter must be used within an EncodedStateFilterProvider');
  }

  return context;
};
import { createContext, FC, ReactNode, useCallback, useContext, useMemo } from 'react'
import { AppTester } from '../../../supporting'
import type { SortingTypeIndex } from '../../constants/sortingType'
import { DisplayType } from '../../constants/displayType'
import useQueryState from "../../hooks/useQueryState";

const DATE_SORT = 1;
const ORG_SORT = 3;
const POP_SORT = 2;
const ACT_SORT = 20;
const MEDIA_ALL = 50;
//const NAME_SORT = 3;
//const NULL_SORT = 0;

type EncodedStateFilterContextT = {
  sorting: SortingTypeIndex;
  tagIds: string[];
  categoryIds: string[];
  display: DisplayType;
  coords: string;
  zoom: number;
  setSorting: (sorting: SortingTypeIndex) => void;
  setZoom: (zoom: number) => void;
  setDisplay: (display: DisplayType) => void;
  setCoords: (coords: string) => void;
  setTagIds: (tags: string[]) => void;
  setCategoryIds: (categoryIds: string[]) => void;
};

const getInitialSortingBase = (): SortingTypeIndex | null => {
  if (typeof window !== 'undefined') {
    if (AppTester.isDeziiignContext()) {
      return DATE_SORT;
    }
    if (window.location.pathname === '/feed') {
      return DATE_SORT;
    }
    // if (window.location.search?.includes("tab=edu-channels")) {
    //   return MEDIA_ALL;
    // }

    if (AppTester.isExamHSE()) {
      return POP_SORT;
    }

    if (AppTester.isLectureMediiia() || AppTester.isStartHSE()) {
      return DATE_SORT;
    }

    if (window.location.pathname.startsWith('/teams/') || window.location.pathname.startsWith('/special')) {
      return null;
    }

    const storedSorting: unknown = localStorage.getItem('brg_sorting');
    if (storedSorting) {
      return storedSorting as SortingTypeIndex;
    }

    return AppTester.isSuperPuper() || AppTester.isLMSHSE() ? DATE_SORT : ACT_SORT;
  }
  return ACT_SORT;
}

const initialCoordsBase = '53.7091468307233,37.04320818185807';

const EncodedStateFilterContext = createContext<EncodedStateFilterContextT>({
  sorting: ACT_SORT,
  tagIds: [],
  categoryIds: [],
  display: DisplayType.grid,
  coords: initialCoordsBase,
  zoom: 0,
  setSorting: () => undefined,
  setZoom: () => undefined,
  setDisplay: () => undefined,
  setCoords: () => undefined,
  setTagIds: () => undefined,
  setCategoryIds: () => undefined,
});

type PropsT = {
  encodedState: string | null;
  initialSorting?: SortingTypeIndex;
  initialZoom?: number;
  initialDisplay?: DisplayType;
  initialCoords?: number[];
  initialTagIds?: string[],
  initialCategoryIds?: string[],
  children?: ReactNode
};

export const EncodedStateFilterProvider: FC<PropsT> = ({
  children,
  encodedState,
  initialSorting = getInitialSortingBase(),
  initialZoom = 5,
  initialDisplay = DisplayType.grid,
  initialCoords = initialCoordsBase,
  initialTagIds = [],
  initialCategoryIds = [],
}) => {

  const [state, setQueryParams] = useQueryState();

  const getInitialSorting = useMemo(() => {
    if (initialSorting) {
      return initialSorting;
    }
    const baseSorting = getInitialSortingBase();
    return baseSorting || ACT_SORT;
  }, [initialSorting]);

  const defaultEncodedState = useMemo(() => ({
    sorting: getInitialSorting,
    display: initialDisplay,
    coords: initialCoords,
  }), [getInitialSorting, initialZoom, initialDisplay, initialCoords, initialTagIds, initialCategoryIds]);

  const decodedState = useMemo(
    () => state,
    [encodedState, defaultEncodedState]
  );

  const sorting = useMemo(() => decodedState.sorting || getInitialSorting, [decodedState, getInitialSorting]);
  const zoom = useMemo(() => decodedState.zoom || initialZoom, [decodedState, initialZoom]);
  const display = useMemo(() => decodedState.display || initialDisplay, [decodedState, initialDisplay]);
  const coords = useMemo(() => decodedState.coords || initialCoords, [decodedState, initialCoords]);
  const tagIds = useMemo(() => decodedState.tagIds || initialTagIds, [decodedState, initialTagIds]);
  const categoryIds = useMemo(() => decodedState.categoryIds || initialCategoryIds, [decodedState, initialCategoryIds]);

  const handleChange = useCallback((payload: Partial<EncodedStateFilterContextT>) => {
    setQueryParams({
      sorting,
      ...(display === 'map' && { display }),
      ...payload,
    }, { replace: true });
  }, [
    sorting,
    display,
    defaultEncodedState,
  ]);

  const setSorting = useCallback((sorting: SortingTypeIndex) => {
    if ([1, 2, 20, 30].includes(sorting)) {
      localStorage.setItem('brg_sorting', String(sorting));
    }
    handleChange({ sorting })
  }, [handleChange]);
  const setZoom = useCallback((zoom: number) => handleChange({ zoom }), [handleChange]);
  const setDisplay = useCallback((display: DisplayType) => handleChange({ display }), [handleChange]);
  const setCoords = useCallback((coords: string) => handleChange({ coords }), [handleChange]);
  const setTagIds = useCallback((tagIds: string[]) => handleChange({ tagIds }), [handleChange]);
  const setCategoryIds = useCallback((categoryIds?: string[]) => handleChange({ categoryIds }), [handleChange]);

  const value = useMemo(() => ({
    sorting,
    zoom,
    display,
    coords: coords.split(','),
    tagIds,
    categoryIds,
    setSorting,
    setZoom,
    setDisplay,
    setCoords,
    setTagIds,
    setCategoryIds,
  }), [
    sorting,
    zoom,
    display,
    coords,
    tagIds,
    categoryIds,
    setSorting,
    setZoom,
    setDisplay,
    setCoords,
    setTagIds,
    setCategoryIds,
  ]);

  return (
    <EncodedStateFilterContext.Provider value={value}>
      {children}
    </EncodedStateFilterContext.Provider>
  );
};

export const useEncodedStateFilter = (): EncodedStateFilterContextT => {
  const context = useContext(EncodedStateFilterContext);

  if (context == null) {
    throw new Error('useEncodedStateFilter must be used within an EncodedStateFilterProvider');
  }

  return context;
};
